import { useEffect, useState, type FormEvent } from "react";

import { useUpdateProject } from "../app/queries";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Alert, AlertDescription } from "./ui/alert";
import { Field, FieldGroup, FieldLabel } from "./ui/field";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "./ui/toggle-group";
import { toast } from "sonner";
import type { Project } from "../lib/api";
import { SUGGESTED_COLORS } from "../lib/colors";

interface EditProjectModalProps {
  project: Project;
  open: boolean;
  onClose: () => void;
  onSaved: (project: Project) => void;
}

// EditProjectModal lets the user rename, recolor, or update the description
// of an existing project. It submits via PATCH and surfaces server-side
// validation errors inline.
export function EditProjectModal({ project, open, onClose, onSaved }: EditProjectModalProps) {
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description);
  const [color, setColor] = useState(project.color);
  const update = useUpdateProject(project.id);

  useEffect(() => {
    if (open) {
      setName(project.name);
      setDescription(project.description);
      setColor(project.color);
    }
  }, [open, project]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    try {
      const next = await update.mutateAsync({
        name: name.trim(),
        description: description.trim(),
        color,
      });
      toast.success(`Saved ${next.name}`);
      onSaved(next);
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to save project";
      toast.error(message);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next: boolean) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit project</DialogTitle>
          <DialogDescription>
            Update the project's name, description, or accent color.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="edit-name">Display name</FieldLabel>
              <Input
                id="edit-name"
                required
                maxLength={120}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="edit-desc">Description</FieldLabel>
              <Textarea
                id="edit-desc"
                rows={3}
                maxLength={500}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel>Accent color</FieldLabel>
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className="inline-block size-5 rounded-sm border border-border"
                  style={{ background: color }}
                  aria-hidden="true"
                />
                <ToggleGroup
                  value={[color]}
                  onValueChange={(value: string[]) => value[0] && setColor(value[0])}
                  aria-label="Accent color"
                  className="flex-wrap gap-1.5"
                >
                  {SUGGESTED_COLORS.map((c) => (
                    <ToggleGroupItem
                      key={c}
                      value={c}
                      className="size-5 rounded-sm border border-border p-0 data-[state=on]:ring-2 data-[state=on]:ring-ring data-[state=on]:ring-offset-2 data-[state=on]:ring-offset-card"
                      aria-label={`Color ${c}`}
                      style={{ background: c }}
                    />
                  ))}
                </ToggleGroup>
              </div>
            </Field>
            {update.isError ? (
              <Alert variant="destructive">
                <AlertDescription>{update.error.message}</AlertDescription>
              </Alert>
            ) : null}
          </FieldGroup>
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}